// api/connectionsClient.js
import axios from 'axios';
import TokenService from '../services/token.service';
import { extractDetailMessage } from '../utils/errorDetail';

/**
 * Cliente HTTP configurado para Connections Service (Meta/WhatsApp integrations)
 * Base URL: Configurado desde variable de entorno
 */
const connectionsClient = axios.create({
  baseURL: process.env.REACT_APP_CONNECTIONS_API_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor para agregar JWT token y x-account-id automáticamente.
 *
 * El header x-account-id es obligatorio en las rutas operacionales del
 * integration_service desde Sub-epic 28.4 (Google) y D.5 (billing) —
 * ``authorize_account_from_header`` en el backend rechaza con 422 cuando
 * falta. Lo leemos del mismo localStorage que mantiene OrganizationContext
 * (key ``currentAccountId``) para que el header siempre refleje la cuenta
 * activa que el usuario seleccionó. Si el contexto aún no está cargado
 * (login en curso, primer render) no enviamos el header — dejamos que el
 * backend devuelva 422 en lugar de inventar un account id.
 */
connectionsClient.interceptors.request.use(
  (config) => {
    // Don't overwrite an Authorization header already set by the caller
    // (e.g. the redeem wizard passes a temporary JWT when there's no Cognito session).
    if (!config.headers.Authorization) {
      const token = TokenService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    const accountId = localStorage.getItem('currentAccountId');
    if (accountId) {
      config.headers['x-account-id'] = accountId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor para manejar errores de respuesta
 */
connectionsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Token expirado o inválido
    if (error.response?.status === 401) {
      console.error('❌ Token inválido o expirado en Connections Service');
      // No redirigir automáticamente, dejar que el componente maneje el error
    }

    // Log de errores para debugging
    if (error.response) {
      console.error('Connections API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Helper para manejar errores de API
 */
export const handleApiError = (error) => {
  if (error.response) {
    const detail = error.response.data?.detail;
    return {
      status: error.response.status,
      message: error.response.data?.message || (detail && extractDetailMessage(detail)) || 'Error en el servidor',
      data: error.response.data
    };
  } else if (error.request) {
    return {
      status: 0,
      message: 'No se pudo conectar con el servidor'
    };
  } else {
    return {
      status: 0,
      message: error.message || 'Error desconocido'
    };
  }
};

export default connectionsClient;
